import { useState } from "react";
import { z } from "zod";
import "./Modal.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

// Validation schema
const eventSchema = z
  .object({
    title: z.string().min(1, "Event title is required"),
    description: z.string().optional(),
    date: z.string().min(1, "Start date is required"),
    startTime: z.string().min(1, "Start time is required"),
    endDate: z.string().min(1, "End date is required"),
    endTime: z.string().min(1, "End time is required"),
  })
  .refine(
    (data) => {
      const start = new Date(`${data.date}T${data.startTime}`);
      const end = new Date(`${data.endDate}T${data.endTime}`);
      return start <= end;
    },
    {
      message: "Start date/time cannot be after end date/time",
      path: ["endDate"],
    }
  )
  .refine(
    (data) => {
      if (data.date === data.endDate) {
        return data.startTime < data.endTime;
      }
      return true;
    },
    {
      message: "Start time must be before end time if dates are same",
      path: ["endTime"],
    }
  );

export default function Modal({ isOpen, onClose, onSubmit, formData, setFormData }) {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const initialFormState = {
    title: "",
    description: "",
    date: "",
    startTime: "",
    endDate: "",
    endTime: "",
  };

  const handleClose = () => {
    setFormData(initialFormState);
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  const handleSave = async () => {
    setErrors({});
    const result = eventSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors = {};
      result.error.errors.forEach((err) => {
        fieldErrors[err.path[0]] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setLoading(true);
    try {
      await onSubmit();
      handleClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="ps-3">
          <button className="modal-close btn-close FontAwesomeIcon fw-lighter " onClick={handleClose}>
            {/* <FontAwesomeIcon className="thin-xmark" 
          icon={faXmark}
          onClick={handleClose}
           /> */}
            <i className="bi bi-x fs-4" onClick={handleClose}></i>
          </button>
          <h5
            style={{
              fontSize: "1rem",
              fontWeight: 600,
              lineHeight: 1
            }}
          >
            Add Event</h5>
        </div>
        <hr />

        {/* div for padding */}
        <div className="px-3">
          {/* Title */}
          <label>
            Event title <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            placeholder="Event Title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          />
          {errors.title && <p className="text-danger small">{errors.title}</p>}

          {/* Description */}
          <label>Description</label>
          <textarea
            placeholder="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />

          {/* Start Date */}
          <div className="row">
            {/* div for start date */}
            <div className="col-md-6">
              <label>Start Date</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              />
              {errors.date && <p className="text-danger small">{errors.date}</p>}
            </div>

            {/* Start Time */}
            {/* div for start time */}
            <div className="col-md-6">
              <label>Start Time</label>
              <input
                type="time"
                value={formData.startTime}
                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
              />
              {errors.startTime && <p className="text-danger small">{errors.startTime}</p>}
            </div>
          </div>

          {/* End Date */}
          <div className="row">
            {/* end date div */}
            <div className="col-md-6">
              <label>End Date</label>
              <input
                type="date"
                value={formData.endDate || formData.date}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
              />
              {errors.endDate && <p className="text-danger small">{errors.endDate}</p>}
            </div>

            {/* End Time */}
            {/* end time div */}
            <div className="col-md-6">
              <label>End Time</label>
              <input
                type="time"
                value={formData.endTime}
                onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
              />
              {errors.endTime && <p className="text-danger small">{errors.endTime}</p>}
            </div>
          </div>

          {/* Save Button */}
          <div className="d-flex justify-content-end ">
            <button
              className="btn btn-default d-flex align-items-center justify-content-center"
              onClick={handleSave}
              disabled={loading}
            >
              {loading && (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
              )}
              {loading ? "Saving..." : "Save Event"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
